export const PHONE_MEDIA_QUERY =
  '(pointer: coarse) and (max-width: 600px), (pointer: coarse) and (max-height: 600px) and (max-width: 1024px)';

export const MOBILE_PANEL_SELECTOR =
  ':scope > :is(.owned-villages, .build-menu, .legacy-player-profile, .legacy-vip-info, .legacy-level-information, .legacy-player-settings, .legacy-online-players, .legacy-help, .legacy-wardrobe, .legacy-chat-log, .legacy-juggling)';
function panelIsOpen(element: HTMLElement): boolean {
  return !element.hidden && (!(element instanceof HTMLDialogElement) || element.open);
}

type Movement = Readonly<{ x: number; y: number }>;
type MobileActions = Readonly<{
  cancelPlacement?: () => void;
  phoneChanged?: (phone: boolean) => void;
  inputBlocked?: (blocked: boolean) => void;
  panelHidden?: (hidden: boolean) => void;
}>;

/** Additive phone chrome, reusing the original toolbar actions and shared panels. */
export class MobileWorldControls {
  private readonly media = window.matchMedia(PHONE_MEDIA_QUERY);
  private readonly joystick = document.createElement('button');
  private readonly bar = document.createElement('nav');
  private readonly placementBar = document.createElement('section');
  private readonly rotatePlacement = document.createElement('button');
  private readonly menu = document.createElement('section');
  private readonly menuButton = document.createElement('button');
  private readonly returnButton = document.createElement('button');
  private readonly chatButton = document.createElement('button');
  private readonly challengesButton = document.createElement('button');
  private readonly observer: MutationObserver;
  private pointerId: number | undefined;
  private phone = false;
  private blocked = false;
  private desktopPanelHidden = false;
  private challengesOpen = false;
  private readonly abort = new AbortController();
  private readonly changed = (): void => {
    this.release();
    if (this.media.matches !== this.phone) {
      if (this.media.matches) {
        this.desktopPanelHidden = this.root.classList.contains('world-view--main-window-hidden');
        this.hidePanel(true);
      } else {
        this.challengesOpen = false;
        this.hidePanel(this.desktopPanelHidden);
        this.menu.hidden = true;
        this.root.classList.remove('world-view--phone-panel', 'world-view--phone-inventory');
      }
      this.phone = this.media.matches;
      this.actions.phoneChanged?.(this.phone);
    }
    this.root.classList.toggle('world-view--phone', this.phone);
    this.bar.hidden = !this.phone;
    this.sync();
  };
  private readonly visibilityChanged = (): void => {
    if (document.visibilityState !== 'visible') this.release();
  };

  constructor(
    private readonly root: HTMLElement,
    private readonly movementChanged: (movement: Movement) => void,
    private readonly actions: MobileActions = {},
  ) {
    const options = { signal: this.abort.signal };
    this.bar.className = 'mobile-bar';
    this.bar.setAttribute('aria-label', 'Phone game controls');
    this.menuButton.type = this.returnButton.type = 'button';
    this.menuButton.textContent = 'Menu';
    this.menuButton.setAttribute('aria-label', 'Open game menu');
    this.menuButton.setAttribute('aria-haspopup', 'dialog');
    this.menuButton.setAttribute('aria-controls', 'mobile-game-menu');
    this.returnButton.textContent = '‹ World';
    this.returnButton.setAttribute('aria-label', 'Return to world');
    this.returnButton.addEventListener('click', () => this.returnToWorld(), options);
    this.menuButton.addEventListener('click', () => this.openMenu(), options);
    const title = document.createElement('strong');
    title.textContent = 'NORD';
    title.className = 'mobile-bar__title';
    const chat = this.chatButton;
    chat.type = 'button';
    chat.textContent = 'Chat';
    chat.setAttribute('aria-label', 'Open Chat');
    chat.addEventListener(
      'click',
      () => {
        const toolbar = this.root.querySelector<HTMLButtonElement>('.world-hud__chat');
        const shortcut = this.root.querySelector<HTMLButtonElement>('.world-chat-shortcut');
        if (toolbar !== null && !toolbar.disabled && shortcut !== null && !shortcut.disabled)
          this.navigate(shortcut, true);
      },
      options,
    );
    this.bar.append(this.returnButton, this.menuButton, title, chat);
    this.menu.className = 'mobile-menu';
    this.menu.id = 'mobile-game-menu';
    this.menu.setAttribute('role', 'dialog');
    this.menu.setAttribute('aria-modal', 'true');
    this.menu.setAttribute('aria-label', 'Game menu');
    this.menu.hidden = true;
    this.challengesButton.type = 'button';
    this.challengesButton.setAttribute('aria-label', 'Daily challenges and weekly streak');
    this.challengesButton.innerHTML =
      '<img src="/legacy/build-menu/products/skattkista.jpg" alt=""><span>Daily challenges</span><small>Weekly streak</small>';
    this.challengesButton.addEventListener(
      'click',
      () => {
        const panel = this.root.querySelector<HTMLDetailsElement>(':scope > .daily-challenges');
        if (!panel || panel.hidden) return;
        this.menu.hidden = true;
        this.root
          .querySelector<HTMLButtonElement>(
            ':scope > .daily-challenges-dock > .daily-challenges-reminder',
          )
          ?.click();
        this.hidePanel(true);
        this.challengesOpen = true;
        panel.open = true;
        this.sync();
        panel.querySelector('summary')?.focus();
      },
      options,
    );
    this.joystick.className = 'mobile-joystick mobile-trackpad';
    this.joystick.type = 'button';
    this.joystick.setAttribute('aria-label', 'Move your avatar');
    this.joystick.setAttribute('aria-describedby', 'mobile-movement-hint');
    this.joystick.innerHTML =
      '<span class="mobile-joystick__thumb" aria-hidden="true"></span><span id="mobile-movement-hint" class="mobile-joystick__hint">Hold & drag to walk</span>';
    this.joystick.addEventListener(
      'pointerdown',
      (event) => {
        if (
          !this.phone ||
          this.blocked ||
          this.pointerId !== undefined ||
          event.pointerType !== 'touch'
        )
          return;
        event.preventDefault();
        this.pointerId = event.pointerId;
        try {
          this.joystick.setPointerCapture(event.pointerId);
        } catch {
          /* Pointer may already be cancelled. */
        }
        this.move(event);
      },
      options,
    );
    this.joystick.addEventListener(
      'pointermove',
      (event) => {
        if (event.pointerId === this.pointerId) this.move(event);
      },
      options,
    );
    for (const type of ['pointerup', 'pointercancel', 'lostpointercapture'] as const) {
      this.joystick.addEventListener(
        type,
        (event) => {
          if (event.pointerId === this.pointerId) this.release();
        },
        options,
      );
    }
    for (const type of ['blur', 'pagehide', 'resize', 'orientationchange'])
      window.addEventListener(type, this.release, options);
    document.addEventListener('visibilitychange', this.visibilityChanged, options);
    this.placementBar.className = 'mobile-placement';
    this.placementBar.setAttribute('aria-label', 'Building placement controls');
    this.placementBar.hidden = true;
    this.rotatePlacement.type = 'button';
    this.rotatePlacement.textContent = 'Rotate';
    this.rotatePlacement.setAttribute('aria-label', 'Rotate placement');
    this.rotatePlacement.addEventListener(
      'click',
      () =>
        this.root.querySelector<HTMLButtonElement>('.build-menu [data-action="rotate"]')?.click(),
      options,
    );
    const cancelPlacement = document.createElement('button');
    cancelPlacement.type = 'button';
    cancelPlacement.textContent = 'Cancel';
    cancelPlacement.setAttribute('aria-label', 'Cancel placement');
    cancelPlacement.addEventListener('click', () => this.actions.cancelPlacement?.(), options);
    const placementHint = document.createElement('span');
    placementHint.textContent = 'Tap the ground to place';
    this.placementBar.append(placementHint, this.rotatePlacement, cancelPlacement);
    this.root.addEventListener(
      'click',
      (event) => {
        if (!this.phone || !(event.target instanceof Element)) return;
        const product = event.target.closest('.build-menu__product[aria-pressed="true"]');
        const edit = event.target.closest(
          '.build-menu [data-action="pickup"], .build-menu [data-action="sell"]',
        );
        if (product !== null || edit !== null) this.returnToWorld();
      },
      options,
    );
    this.root.addEventListener(
      'keydown',
      (event) => {
        if (!this.phone || !this.blocked || this.root.querySelector('dialog[open]') !== null)
          return;
        if (event.key === 'Escape') {
          event.preventDefault();
          this.returnToWorld();
        }
        if (event.key !== 'Tab') return;
        const focusable = [
          ...this.root.querySelectorAll<HTMLElement>(
            'button:not(:disabled), a[href], input, select, textarea, summary, [tabindex="0"]',
          ),
        ].filter((element) => element.getClientRects().length > 0 && !element.closest('[inert]'));
        const first = focusable[0];
        const last = focusable.at(-1);
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      },
      options,
    );
    this.observer = new MutationObserver(() => this.sync());
    this.observer.observe(this.root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden', 'class', 'open', 'aria-pressed', 'disabled'],
    });
    this.media.addEventListener('change', this.changed);
    this.root.append(this.joystick, this.menu, this.bar, this.placementBar);
    this.changed();
  }

  private hidePanel(hidden: boolean): void {
    if (this.root.classList.contains('world-view--main-window-hidden') === hidden) return;
    if (this.actions.panelHidden !== undefined) this.actions.panelHidden(hidden);
    else this.root.classList.toggle('world-view--main-window-hidden', hidden);
  }

  private openMenu(): void {
    this.release();
    this.challengesOpen = false;
    this.hidePanel(true);
    this.root.classList.remove('world-view--phone-inventory');
    const grid = document.createElement('div');
    grid.className = 'mobile-menu__grid';
    for (const original of this.root.querySelectorAll<HTMLButtonElement>('.world-hud button')) {
      if (original.hidden || original.classList.contains('world-hud__placeholder')) continue;
      const label =
        original.getAttribute('aria-label') ?? (original.title || original.textContent || '');
      if (!label.trim()) continue;
      const button = document.createElement('button');
      button.type = 'button';
      button.disabled = original.disabled;
      button.setAttribute('aria-label', label);
      const image = original.querySelector('img');
      if (image !== null) button.append(image.cloneNode(true));
      const caption = document.createElement('span');
      caption.textContent = original.classList.contains('world-hud__coins') ? 'Nord Coins' : label;
      button.append(caption);
      button.addEventListener('click', () => this.navigate(original));
      grid.append(button);
    }
    grid.prepend(this.challengesButton);
    if (this.root.querySelector('.legacy-resource-hud') !== null) {
      const inventory = document.createElement('button');
      inventory.type = 'button';
      inventory.setAttribute('aria-label', 'Backpack');
      inventory.innerHTML = '<img src="/legacy/resources/carrot.png" alt=""><span>Backpack</span>';
      inventory.addEventListener('click', () => {
        this.menu.hidden = true;
        this.hidePanel(true);
        this.root.classList.add('world-view--phone-inventory');
        this.sync();
      });
      grid.prepend(inventory);
    }
    this.menu.replaceChildren(grid);
    this.menu.hidden = false;
    this.sync();
    grid.querySelector<HTMLButtonElement>('button:not(:disabled):not([hidden])')?.focus();
  }

  private navigate(original: HTMLButtonElement, explicitActivation = false): void {
    this.menu.hidden = true;
    this.challengesOpen = false;
    const panels = [...this.root.querySelectorAll<HTMLElement>(MOBILE_PANEL_SELECTOR)];
    const preserved = new Set(panels.filter(panelIsOpen));
    // Returning to terrain hides, rather than closes, the current panel. Reopening it
    // must not invoke the desktop toggle and accidentally close it (or its preview).
    // Chat's shortcut activates a tab rather than toggling the panel; invoke it even
    // when a preserved Forum panel is already expanded, including hidden terrain state.
    if (explicitActivation || original.getAttribute('aria-expanded') !== 'true') original.click();
    const panel = panels.find(panelIsOpen);
    const navigatedToPanel =
      original.getAttribute('aria-expanded') === 'true' ||
      !this.root.classList.contains('world-view--main-window-hidden') ||
      (panel !== undefined && !preserved.has(panel));
    // A one-shot action must not reveal whichever side panel was preserved on terrain.
    if (panel === undefined || !navigatedToPanel) {
      this.returnToWorld();
      return;
    }
    this.hidePanel(false);
    this.sync();
    panel.tabIndex = -1;
    panel.focus();
  }

  returnToWorld(): void {
    this.menu.hidden = true;
    this.challengesOpen = false;
    this.hidePanel(true);
    this.root.classList.remove('world-view--phone-inventory');
    this.sync();
    this.menuButton.focus();
  }

  private sync(): void {
    const challenges = this.root.querySelector<HTMLDetailsElement>(':scope > .daily-challenges');
    const challengesAvailable = challenges !== null && !challenges.hidden;
    if (this.challengesButton.hidden === challengesAvailable)
      this.challengesButton.hidden = !challengesAvailable;
    const challengesVisible =
      this.phone &&
      this.challengesOpen &&
      challengesAvailable &&
      challenges.open &&
      this.root.classList.contains('world-view--main-window-hidden');
    if (this.challengesOpen && !challengesVisible) {
      this.challengesOpen = false;
      if (challenges?.contains(document.activeElement)) this.menuButton.focus();
    }
    this.root.classList.toggle('world-view--phone-challenges', challengesVisible);
    const toolbarChat = this.root.querySelector<HTMLButtonElement>('.world-hud__chat');
    const chatShortcut = this.root.querySelector<HTMLButtonElement>('.world-chat-shortcut');
    const chatDisabled = toolbarChat?.disabled !== false || chatShortcut?.disabled !== false;
    if (this.chatButton.disabled !== chatDisabled) this.chatButton.disabled = chatDisabled;
    const panelVisible =
      challengesVisible ||
      (this.phone &&
        !this.root.classList.contains('world-view--main-window-hidden') &&
        [...this.root.querySelectorAll<HTMLElement>(MOBILE_PANEL_SELECTOR)].some(panelIsOpen));
    this.root.classList.toggle('world-view--phone-panel', panelVisible);
    const modalVisible = [
      ...this.root.querySelectorAll<HTMLElement>('dialog[open], [role="dialog"]'),
    ].some(
      (element) => element !== this.menu && element.getClientRects().length > 0 && !element.hidden,
    );
    const blocked =
      this.phone &&
      (!this.menu.hidden ||
        panelVisible ||
        modalVisible ||
        this.root.classList.contains('world-view--phone-inventory'));
    if (blocked !== this.blocked) {
      this.release();
      this.blocked = blocked;
      this.actions.inputBlocked?.(blocked);
    }
    this.root.querySelector<HTMLCanvasElement>('.world-canvas')?.toggleAttribute('inert', blocked);
    if (this.joystick.hidden !== (!this.phone || blocked))
      this.joystick.hidden = !this.phone || blocked;
    if (this.returnButton.hidden !== !blocked) this.returnButton.hidden = !blocked;
    this.menuButton.setAttribute('aria-expanded', String(!this.menu.hidden));
    const selected = this.root.querySelector(
      '.build-menu__product[aria-pressed="true"], .build-menu [data-action][aria-pressed="true"]',
    );
    const placementVisible = this.phone && !blocked && selected !== null;
    if (this.placementBar.hidden === placementVisible) this.placementBar.hidden = !placementVisible;
    const rotate = this.root.querySelector<HTMLButtonElement>('.build-menu [data-action="rotate"]');
    const rotateDisabled = rotate?.disabled !== false;
    if (this.rotatePlacement.disabled !== rotateDisabled)
      this.rotatePlacement.disabled = rotateDisabled;
  }

  private move(event: PointerEvent): void {
    event.preventDefault();
    const bounds = this.joystick.getBoundingClientRect();
    // Scale travel with the rendered original-art surface, including compact phones.
    const travel = Math.min(bounds.width, bounds.height) * 0.3;
    const x = (event.clientX - bounds.left - bounds.width / 2) / travel;
    const y = (event.clientY - bounds.top - bounds.height / 2) / travel;
    const length = Math.hypot(x, y);
    const movement =
      length < 0.18 ? { x: 0, y: 0 } : { x: x / Math.max(1, length), y: y / Math.max(1, length) };
    this.joystick.style.setProperty('--thumb-x', `${String(movement.x * travel)}px`);
    this.joystick.style.setProperty('--thumb-y', `${String(movement.y * travel)}px`);
    this.movementChanged(movement);
  }

  private readonly release = (): void => {
    const pointerId = this.pointerId;
    this.pointerId = undefined;
    if (pointerId !== undefined) {
      try {
        if (this.joystick.hasPointerCapture(pointerId))
          this.joystick.releasePointerCapture(pointerId);
      } catch {
        /* Capture loss races with cancellation. */
      }
      this.movementChanged({ x: 0, y: 0 });
    }
    this.joystick.style.setProperty('--thumb-x', '0px');
    this.joystick.style.setProperty('--thumb-y', '0px');
  };

  dispose(): void {
    this.release();
    this.observer.disconnect();
    this.abort.abort();
    this.media.removeEventListener('change', this.changed);
    if (this.blocked) this.actions.inputBlocked?.(false);
    this.joystick.remove();
    this.menu.remove();
    this.bar.remove();
    this.placementBar.remove();
    this.root.querySelector('.world-canvas')?.removeAttribute('inert');
    this.root.classList.remove(
      'world-view--phone',
      'world-view--phone-panel',
      'world-view--phone-challenges',
    );
  }
}
